import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import MagneticButton from "@/components/common/MagneticButton";

const packages = [
  {
    id: "basic",
    name: "Basic AI",
    badge: null,
    setup: "₹20,000",
    monthly: "₹999/m",
    bestFor: "Solo founders and small teams getting started with automation.",
    features: [
      "AI WhatsApp Assistant",
      "Smart Lead Capture",
      "Appointment Booking System",
      "Basic CRM Integration",
      "Email Notifications",
      "Mobile Responsive",
      "14 Days Free Support",
    ],
    cta: "Book a Free Consultation",
    href: "#booking",
    highlighted: false,
  },
  {
    id: "launch",
    name: "Launch AI",
    badge: "Most Popular",
    setup: "₹35,000",
    monthly: "₹1,900/m",
    bestFor: "Small businesses ready to launch their AI-powered presence.",
    features: [
      "High-Converting Business Website",
      "AI WhatsApp Assistant",
      "Smart Lead Capture",
      "Appointment Booking System",
      "AI Customer Support",
      "Business Analytics Dashboard",
      "Mobile Responsive Design",
      "Google Analytics Integration",
      "30 Days Free Support",
    ],
    cta: "Book a Free Consultation",
    href: "#booking",
    highlighted: true,
  },
];

export default function Packages() {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    const trigger = sectionRef.current;
    if (!trigger) return;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    gsap.fromTo(
      headerRef.current,
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger === trigger) st.kill();
      });
    };
  }, []);

  return (
    <section id="packages" ref={sectionRef} className="section relative" data-testid="packages-section">
      <div className="absolute inset-0 aurora opacity-50" />
      <div className="container-x relative">
        <div ref={headerRef} className="max-w-3xl mx-auto text-center mb-16">
          <span className="eyebrow justify-center"><span className="dot" /> Packages</span>
          <h2 className="h-display mt-5 text-4xl sm:text-5xl lg:text-6xl text-[var(--body-fg)]">
            Everything you need to <span className="font-serif italic gradient-text">launch</span> with AI.
          </h2>
          <p className="mt-6 text-[var(--body-fg)]/60 leading-relaxed text-base sm:text-lg">
            Transparent pricing: a one-time setup plus a simple monthly retainer. No hidden fees.
          </p>
        </div>

        <div className="max-w-4xl mx-auto relative">
          <div className="pointer-events-none absolute -top-20 -left-20 w-40 h-40 rounded-full bg-orange-400/15 blur-[80px]" />
          <div className="pointer-events-none absolute -bottom-20 -right-20 w-48 h-48 rounded-full bg-amber-300/15 blur-[90px]" />

          <div className="grid md:grid-cols-2 gap-5 items-stretch">
            {packages.map((pkg, i) => (
              <Reveal key={pkg.id} direction="up" delay={0.1 * i} scale={0.96}>
                <article
                  className={`relative glow-border flex flex-col h-full rounded-[2rem] p-7 sm:p-8 glass-strong ${
                    pkg.highlighted ? "ring-1 ring-[var(--card-hover-border)]" : ""
                  }`}
                  style={{ boxShadow: "0 30px 80px -30px var(--brand-glow)" }}
                  data-testid={`package-card-${pkg.id}`}
                >
                  {pkg.badge && (
                    <div
                      className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium shadow-sm"
                      style={{ background: "var(--eyebrow-bg)", border: "1px solid var(--eyebrow-border)", color: "var(--brand)" }}
                    >
                      <Sparkles className="w-3 h-3" />
                      {pkg.badge}
                    </div>
                  )}

                  <h3 className="text-[var(--body-fg)] text-3xl font-semibold tracking-tight text-center">{pkg.name}</h3>

                  <div className="mt-6 text-center">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl sm:text-5xl font-semibold tracking-tight text-[var(--body-fg)]">{pkg.setup}</span>
                    </div>
                    <div className="mt-1 text-sm text-[var(--body-fg)]/55">one-time setup</div>
                    <div
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
                      style={{ background: "var(--eyebrow-bg)", border: "1px solid var(--eyebrow-border)", color: "var(--brand)" }}
                    >
                      then {pkg.monthly}
                    </div>
                  </div>

                  <div className="mt-6 text-center">
                    <div className="text-[var(--body-fg)]/45 font-mono uppercase tracking-widest text-[10px]">Best For</div>
                    <p className="mt-1 text-[var(--body-fg)]/70 leading-snug">{pkg.bestFor}</p>
                  </div>

                  <div className="mt-6 flex-1">
                    <div className="text-[var(--body-fg)]/45 font-mono uppercase tracking-widest text-[10px] mb-4">Includes</div>
                    <ul className="space-y-3">
                      {pkg.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-3 text-sm text-[var(--body-fg)]/80">
                          <span
                            className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                            style={{ background: "var(--eyebrow-bg)", color: "var(--brand)" }}
                          >
                            <Check className="w-3 h-3" />
                          </span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <MagneticButton
                    as="a"
                    href={pkg.href}
                    className="btn-primary mt-8 w-full justify-center group"
                    data-testid={`package-cta-${pkg.id}`}
                    strength={0.25}
                    scale={1.03}
                  >
                    {pkg.cta}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </MagneticButton>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
